'use client';

// Chart shell — owns the canvas the drawers paint into, the caption they
// return, and the ONE cursor every chart in the product shares.
//
// The cursor is the REW graph-cursor idiom: press or hover anywhere on a plot
// and two lines meet under the pointer, a vertical one and a horizontal one,
// each labelled with its value on its own axis. Nothing is snapped to a curve
// and nothing is guessed: the drawer hands back a `PlotDescriptor` (the plot
// box plus the exact scales it drew with — see `ChartPaint`) and the cursor is
// inverted through it. That is what lets this one component serve a log-
// frequency response, a linear impulse response, a room-plan heatmap and a
// spectrogram alike, with no per-chart cursor code and no second copy of the
// pad or axis range to drift.
//
// A chart with a `scrub` also has a STANDING marker on its x axis — the
// frequency the 3D field is showing, the reflection-time filter — drawn in the
// accent (the one hue that means "current position"). A claimed gesture moves
// it; while the gesture lasts the cursor is the marker, and when a finger
// lifts only the marker stays. On a plain chart a finger's cursor stays where
// it was left; a mouse's follows the hover and clears on leave.
//
// Touch is handled by `useScrollSafePointer`, which is where "a finger
// drifting one pixel off the horizontal hands the gesture to the page" was
// fixed for every chart at once.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { markerAtX, readCrosshair, type PlotDescriptor } from '@roomtreatment/charts';
import { useIdempotentState } from '@/lib/useIdempotentState';
import { useChartInView } from '@/components/analytics/useChartInView';
import { EVENTS, track } from '@/lib/analytics';
import { useScrollSafePointer, type GesturePoint, type PointerGestureMode } from '@/lib/useScrollSafePointer';
import { useChartAnalyticsId } from './chartAnalyticsId';
import type { ChartPaint } from './chartCanvas';
import { useTheme } from '@/lib/theme/useTheme';

export interface ChartScrub {
  /** Where the standing marker sits on the x axis, in data units. */
  value: number;
  /** The x value under the pointer, for every point of a claimed gesture
   *  (press, drag, tap). Clamping and rounding are the caller's. */
  onChange: (x: number) => void;
}

interface ChartProps {
  title: string;
  /** Stable analytics id — defaults to `title` when omitted. */
  chartId?: string;
  /** Small text (a string) or a compact control (e.g. an option picker) in
   *  the header's right slot, next to the title. */
  sub?: ReactNode;
  /** Paints the canvas and describes what it painted; null = no data. */
  draw: ((canvas: HTMLCanvasElement) => ChartPaint) | null;
  /** Makes the chart a scrubber: a standing marker at `value`, moved by any
   *  claimed gesture. */
  scrub?: ChartScrub;
  /** Who owns a touch. 'page-scroll' (default) for a chart inside a
   *  scrollable panel; 'capture' for one floating over the 3D view. */
  gesture?: PointerGestureMode;
  /** What to say when `draw` is null. Defaults to "no data", which is right
   *  for a chart that genuinely has nothing to show — but in the advanced
   *  editor an empty chart usually means "analysis hasn't been run yet"
   *  (useAutoSim gates on scanNonce there), and a bare "no data" reads as
   *  broken rather than as a prompt. Callers that know the reason pass it. */
  emptyLabel?: string;
  /** When true, skip the title/sub header row — used by landing showcase charts
   *  that only need the canvas. */
  hideHeader?: boolean;
  /** Shorter canvas, no chart border — landing showcase stack, the phone
   *  scrubber pill. */
  compact?: boolean;
  /** Hide the header sub slot (controls / hints) while keeping the title. */
  hideSub?: boolean;
  /** `data-testid` for the canvas wrap (the element the cursor lives in). */
  testId?: string;
}

interface Size { w: number; h: number }
interface Point { x: number; y: number }

function sameScale(a: PlotDescriptor['x']['scale'], b: PlotDescriptor['x']['scale']): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'log-freq' && b.kind === 'log-freq') {
    return a.fMin === b.fMin && a.fMax === b.fMax && a.plotW === b.plotW;
  }
  if (a.kind === 'linear' && b.kind === 'linear') {
    return a.lo === b.lo && a.hi === b.hi && a.invertY === b.invertY && a.length === b.length;
  }
  return false;
}

/** Structural equality, so a redraw that lays the plot out identically (the
 *  common case — once per scene edit, so once per pointermove of a drag)
 *  does not re-render the cursor overlay. `format` closures are ignored:
 *  a drawer's formatter is a constant of that chart. */
function samePlot(a: PlotDescriptor | null, b: PlotDescriptor | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const fa = a.frame;
  const fb = b.frame;
  if (fa.plotX !== fb.plotX || fa.plotY !== fb.plotY || fa.plotW !== fb.plotW || fa.plotH !== fb.plotH) return false;
  if (!sameScale(a.x.scale, b.x.scale)) return false;
  if (!a.y !== !b.y) return false;
  return !a.y || !b.y || sameScale(a.y.scale, b.y.scale);
}

/** A px in the canvas's content box as a percentage of it. Overlays are
 *  positioned by PERCENT, never px: the editor root runs a CSS `zoom`, and a
 *  px `left` would be scaled by it a second time (the cursor then rendered
 *  ~zoom× left of the finger, the drift growing with x). A percentage
 *  resolves against the rendered box, so it lands on the pixel the drawn
 *  curve occupies on every engine. */
function pct(v: number, total: number): number {
  return total > 0 ? (v / total) * 100 : 0;
}

export default function Chart({
  title, chartId: chartIdProp, sub, draw, scrub, gesture = 'page-scroll',
  emptyLabel = 'no data', hideHeader = false, compact = false, hideSub = false, testId,
}: ChartProps) {
  const registryId = useChartAnalyticsId();
  const chartId = chartIdProp ?? registryId ?? title;
  const chartViewRef = useChartInView(chartId, !!draw);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const lastInteractAtRef = useRef(0);
  const interactDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Idempotent: the redraw effect re-runs on every `draw` identity change and
  // hands back the same caption nearly every time. See useIdempotentState.
  const [caption, setCaption] = useIdempotentState('');
  const [plot, setPlotState] = useState<PlotDescriptor | null>(null);
  // Content-box size of the canvas — the SAME measurement the drawers size
  // their buffer from (canvas.clientWidth/Height), so every overlay maps
  // data↔px exactly like the drawn curve and grid.
  const [size, setSizeState] = useState<Size>({ w: 0, h: 0 });
  // Cursor position in content-box px; null = no cursor.
  const [cursor, setCursor] = useState<Point | null>(null);
  // A claimed gesture is moving the scrub marker right now.
  const [scrubbing, setScrubbing] = useIdempotentState(false);
  // The latest descriptor, readable from inside pointer handlers without
  // waiting for a render.
  const plotRef = useRef<PlotDescriptor | null>(null);

  const setPlot = useCallback((next: PlotDescriptor | null) => {
    plotRef.current = next;
    setPlotState((prev) => (samePlot(prev, next) ? prev : next));
  }, []);
  const setSize = useCallback((next: Size) => {
    setSizeState((prev) => (prev.w === next.w && prev.h === next.h ? prev : next));
  }, []);

  const reportChartInteract = useCallback((kind: 'hover' | 'scrub') => {
    const now = Date.now();
    if (now - lastInteractAtRef.current < 10000) return;
    if (interactDebounceRef.current) clearTimeout(interactDebounceRef.current);
    interactDebounceRef.current = setTimeout(() => {
      lastInteractAtRef.current = Date.now();
      track(EVENTS.chart_interacted, { chart_id: chartId, kind });
    }, 1000);
  }, [chartId]);

  useEffect(() => {
    return () => {
      if (interactDebounceRef.current) clearTimeout(interactDebounceRef.current);
    };
  }, []);

  // Drawers resolve their ink from the canvas's own theme at draw time
  // (canvasInkAt), so a theme flip changes nothing until something redraws.
  // Subscribing here and listing it as an effect dependency is that redraw.
  const theme = useTheme(canvasRef);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const redraw = () => {
      if (draw) {
        const paint = draw(canvas);
        setCaption(paint.caption);
        setPlot(paint.plot);
      } else {
        setCaption('');
        setPlot(null);
      }
      setSize({ w: canvas.clientWidth, h: canvas.clientHeight });
    };
    redraw();
    const ro = new ResizeObserver(redraw);
    ro.observe(canvas);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [draw, theme, setCaption, setPlot, setSize]);

  // Pointer → content-box px. The pointer's position is taken as a FRACTION
  // of the canvas's visual bounding box, then scaled by clientWidth/Height:
  // because (clientX − r.left) and r.width both come from the same
  // getBoundingClientRect, any ancestor CSS `zoom` cancels in the ratio — the
  // same zoom-invariant trick the sliders use. See `pct` for the other half.
  const toCanvasPx = useCallback((p: GesturePoint): Point | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const r = canvas.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return null;
    return {
      x: ((p.clientX - r.left) / r.width) * canvas.clientWidth,
      y: ((p.clientY - r.top) / r.height) * canvas.clientHeight,
    };
  }, []);

  const { handlers, style: gestureStyle } = useScrollSafePointer({
    ref: wrapRef,
    mode: gesture,
    onPoint: (p, claimed) => {
      const px = toCanvasPx(p);
      if (!px) return;
      setCursor(px);
      if (claimed && scrub && plotRef.current) {
        setScrubbing(true);
        const reading = readCrosshair(plotRef.current, px.x, px.y);
        if (reading) scrub.onChange(reading.x);
        reportChartInteract('scrub');
      } else {
        reportChartInteract('hover');
      }
    },
    onRelease: (p) => {
      setScrubbing(false);
      // A scrubber keeps only its marker once a finger lifts: the marker IS
      // the position, and a cursor left behind would show it twice.
      if (scrub && p.pointerType === 'touch') setCursor(null);
    },
    onCancel: () => {
      setScrubbing(false);
      setCursor(null);
    },
    onLeave: () => setCursor(null),
  });

  const reading = useMemo(
    () => (plot && cursor ? readCrosshair(plot, cursor.x, cursor.y) : null),
    [plot, cursor],
  );
  const marker = useMemo(
    () => (plot && scrub ? markerAtX(plot, scrub.value) : null),
    [plot, scrub],
  );
  // While a claimed gesture moves the marker, the cursor is the marker.
  const showMarker = marker != null && !(scrubbing && reading != null);

  return (
    <div ref={chartViewRef}>
      {!hideHeader && (
        // Wraps: a chart whose header carries its own view toggles (smoothing,
        // band width) puts them on a second row under the title rather than
        // shrinking them to fit beside it.
        <div className="relative flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1 pb-0.5">
          <span className="ed-chart-title">
            {title}
          </span>
          {sub && !hideSub && <div className="ed-mono ed-caption">{sub}</div>}
        </div>
      )}
      <div
        ref={wrapRef}
        data-testid={testId}
        {...handlers}
        style={gestureStyle}
        className={
          // bg locked to --ed-bg (the same token the 3D viewport clears to,
          // in either theme) so the chart's ground is identical to the
          // room's ground — no perceptual bounce between the two readouts.
          (compact
            ? 'relative mt-0 h-[120px] w-full overflow-hidden bg-[var(--ed-bg)]'
            : 'relative mt-1.5 h-[170px] w-full overflow-hidden rounded-sm border border-[var(--ed-border)] bg-[var(--ed-bg)]')
          + ' select-none' + (plot ? ' cursor-crosshair' : '')
        }
      >
        <canvas ref={canvasRef} className="h-full w-full" />
        {!draw && (
          <div className="ed-caption absolute inset-0 flex items-center justify-center px-4 text-center italic leading-relaxed">
            {emptyLabel}
          </div>
        )}

        {plot && showMarker && marker && (
          <>
            <div
              aria-hidden
              data-testid="chart-marker"
              className="pointer-events-none absolute w-[1.5px] -translate-x-1/2 bg-[var(--ed-accent)]"
              style={{
                left: `${pct(marker.px, size.w)}%`,
                top: `${pct(plot.frame.plotY, size.h)}%`,
                height: `${pct(plot.frame.plotH, size.h)}%`,
              }}
            />
            <AxisValue axis="x" tone="accent" label={marker.label} at={marker.px} frame={plot.frame} size={size} testId="chart-marker-value" />
          </>
        )}

        {plot && reading && (
          <>
            <div
              aria-hidden
              data-testid="chart-cursor-x"
              className="pointer-events-none absolute w-px bg-[var(--ed-text)] opacity-70"
              style={{
                left: `${pct(reading.px, size.w)}%`,
                top: `${pct(plot.frame.plotY, size.h)}%`,
                height: `${pct(plot.frame.plotH, size.h)}%`,
              }}
            />
            {reading.y != null && (
              <div
                aria-hidden
                data-testid="chart-cursor-y"
                className="pointer-events-none absolute h-px bg-[var(--ed-text)] opacity-70"
                style={{
                  top: `${pct(reading.py, size.h)}%`,
                  left: `${pct(plot.frame.plotX, size.w)}%`,
                  width: `${pct(plot.frame.plotW, size.w)}%`,
                }}
              />
            )}
            <AxisValue axis="x" tone="ink" label={reading.xLabel} at={reading.px} frame={plot.frame} size={size} testId="chart-cursor-x-value" />
            {reading.yLabel != null && (
              <AxisValue axis="y" tone="ink" label={reading.yLabel} at={reading.py} frame={plot.frame} size={size} testId="chart-cursor-y-value" />
            )}
          </>
        )}
      </div>
      {caption && (
        <p className="ed-mono ed-caption mt-1">{caption}</p>
      )}
    </div>
  );
}

// Approximate box of a readout, for keeping it inside the canvas. The box
// auto-sizes; these only decide how far in from the edge its anchor may go.
// Sized for the caption role the readout is set in (12px tabular figures,
// ~0.56em each, 2px padding top and bottom).
const VALUE_CHAR_W = 7;
const VALUE_PAD_W = 8;
const VALUE_H = 16;

/** The value a cursor line is at, printed ON its axis: the x readout sits
 *  over the tick row under the plot, the y readout over the tick column left
 *  of it — the same place the eye already reads that axis. A READOUT, not a
 *  control: no affordances (see AGENTS.md's status-readout rule). */
function AxisValue({
  axis, tone, label, at, frame, size, testId,
}: {
  axis: 'x' | 'y';
  /** `ink` for the cursor, `accent` for the standing marker (current
   *  position in the flow — the accent's one job). */
  tone: 'ink' | 'accent';
  label: string;
  /** Content-box px along the axis. */
  at: number;
  frame: PlotDescriptor['frame'];
  size: Size;
  testId: string;
}) {
  const toneClass = tone === 'accent'
    ? 'bg-[var(--ed-accent)] text-[var(--ed-bg)]'
    : 'bg-[var(--ed-text)] text-[var(--ed-bg)]';
  const base = `pointer-events-none absolute z-[5] whitespace-nowrap rounded-sm px-1 py-[2px] ed-mono text-caption leading-none tabular-nums ${toneClass}`;
  if (axis === 'x') {
    const half = (label.length * VALUE_CHAR_W + VALUE_PAD_W) / 2;
    const x = Math.max(half, Math.min(size.w - half, at));
    return (
      <div
        aria-hidden
        data-testid={testId}
        className={`${base} -translate-x-1/2`}
        style={{ left: `${pct(x, size.w)}%`, top: `${pct(frame.plotY + frame.plotH, size.h)}%`, marginTop: 1 }}
      >
        {label}
      </div>
    );
  }
  const y = Math.max(VALUE_H / 2, Math.min(size.h - VALUE_H / 2, at));
  // Right-aligned to the plot's left edge; if the pad is too narrow for the
  // text the box slides inside the plot rather than off the canvas.
  const w = label.length * VALUE_CHAR_W + VALUE_PAD_W;
  const rightEdge = Math.max(w, frame.plotX);
  return (
    <div
      aria-hidden
      data-testid={testId}
      className={`${base} -translate-x-full -translate-y-1/2`}
      style={{ left: `${pct(rightEdge, size.w)}%`, top: `${pct(y, size.h)}%`, marginLeft: -2 }}
    >
      {label}
    </div>
  );
}
